import { useCallback, useEffect, useRef, useState } from 'react';
import { humanizeApiError, type FinancialConfig, type Integration } from '@lapo/core';
import type { FinancialSectionState, ToastInput } from '@lapo/ui';
import { ApiError, type ApiClient, type FinancialItemDTO } from './auth/apiClient.js';
import type { AuthSession } from './auth/provider.js';
import { API_URL } from './config.js';
import { useIntegrationConfig } from './useIntegrationConfig.js';
import {
  loadPlaidLink,
  storeLinkToken,
  takeStoredLinkToken,
  peekStoredLinkToken,
  clearStoredLinkToken,
  isPlaidOAuthReturn,
} from './plaidLink.js';
import { hostedPlaidConnect } from './hostedPlaid.js';
import { isTauri } from './shell/shell.js';

/**
 * Owns Financial Management (Plaid) — the config that gates the "Finances" nav entry, the list of
 * connected bank items, and every action the Integrations → Financial Management detail page drives:
 * connect a bank (Plaid Link token → public_token exchange, web) or open Plaid HOSTED Link in the
 * system browser + poll it to completion (native), reconnect an item (Plaid update mode), disconnect
 * an item, the Monarch CSV import, and the cardholder-alias editor. Behavior-preserving extraction from
 * App.tsx: the connect/reconnect web/native split, the hosted-link stash-and-poll (refocus auto-finish,
 * native cold-start resume, and the OAuth-return resume), the double-finish guard, and the auto-sync
 * after a reconnect are all reproduced exactly.
 *
 * `useIntegrationConfig` (the shared Integrations-hub state machine) lives inside the hook — its
 * `enabled` (App's `integrationsEnabled` = `!!session && !!API_URL`) and `visible` (App's `settingsOpen`)
 * gates are passed in, as is `setHubIntegrations` (the run splices the refreshed hub row). Other deps are
 * `api`, `session`, and `pushToast` — never reached out of App. `session` null (signed out) keeps
 * everything empty/local. `refreshFinancialFromHub` is returned so the (App-owned) integration-detail
 * refresh effect can re-pull config + items when the Financial Management page opens.
 */
export interface UseFinancialDeps {
  api: ApiClient;
  session: AuthSession | null;
  pushToast: (t: ToastInput) => number;
  /** Master gate for the config load — App passes `!!session && !!API_URL`. */
  integrationsEnabled: boolean;
  /** Settings visibility — every rising edge triggers a background config refresh (App's `settingsOpen`). */
  settingsOpen: boolean;
  /** Splice the refreshed hub integration row after a "Sync now" run (App owns the hub list). */
  setHubIntegrations: (updater: (prev: Integration[]) => Integration[]) => void;
}

export interface UseFinancial {
  // State the detail page + nav read.
  financialConfig: FinancialConfig | null;
  financialItems: FinancialItemDTO[];
  financialConnecting: boolean;
  reconnectingItemId: string | undefined;
  hostedPending: boolean;
  financialImporting: boolean;
  /** The Settings-modal section object (null until config loads / signed out). */
  financial: FinancialSectionState | undefined;
  // Config save/run (from the shared integration-config state machine).
  onSaveFinancial: (next: FinancialConfig) => Promise<void>;
  onRunFinancial: () => Promise<void>;
  // Actions.
  onConnectFinancial: () => Promise<void>;
  onReconnectFinancial: (itemId: string) => Promise<void>;
  onCancelHostedConnect: () => void;
  onImportMonarch: (file: File) => Promise<void>;
  onDisconnectFinancialItem: (itemId: string) => Promise<void>;
  loadFinancialCardholders: () => Promise<{ raw: string; alias: string }[]>;
  onSaveFinancialCardholder: (raw: string, alias: string) => Promise<void>;
  /** Re-pull config + items — the integration-detail refresh effect calls this when the page opens. */
  refreshFinancialFromHub: () => Promise<void>;
}

export function useFinancial({
  api,
  session,
  pushToast,
  integrationsEnabled,
  settingsOpen,
  setHubIntegrations,
}: UseFinancialDeps): UseFinancial {
  // Financial Management: load config (gates the "Finances" nav entry) + connected banks. The
  // dashboard itself self-fetches from `api`. The bank items ride along with the config load.
  const [financialItems, setFinancialItems] = useState<FinancialItemDTO[]>([]);
  const [financialConnecting, setFinancialConnecting] = useState(false);
  // The item currently being reconnected (Plaid update mode), so its row can show a spinner + disable.
  const [reconnectingItemId, setReconnectingItemId] = useState<string | undefined>(undefined);
  // Set while a reconnect (not a fresh connect) is in flight, so refreshAfterConnect can auto-run a sync
  // once the re-auth lands — pulling fresh balances + a new "last updated" time right away.
  const reconnectSyncRef = useRef(false);
  // Native hosted-link: a bank connect was opened in the system browser but not yet confirmed, so the
  // panel can offer a "Finish connecting" button (in case the auto-poll timed out on a slow OAuth).
  const [hostedPending, setHostedPending] = useState(false);
  // Guards against a double success toast: several pollers (in-flight, refocus, cold-start) can resolve
  // the same hosted session near-simultaneously — only the first should announce + refresh.
  const hostedDoneRef = useRef(false);
  const financialState = useIntegrationConfig<FinancialConfig>({
    enabled: integrationsEnabled,
    visible: settingsOpen,
    load: async (signal) => {
      const [c, items] = await Promise.all([api.getFinancialConfig(), api.listFinancialItems().catch(() => [])]);
      if (!signal.aborted) setFinancialItems(items);
      return c;
    },
    save: async (next) => {
      const saved = await api.putFinancialConfig(next);
      pushToast({ tone: 'success', message: 'Financial Management settings saved.' });
      return saved;
    },
    onSaveError: (err) => pushToast({ tone: 'error', title: 'Could not save', message: err.message }),
    run: async () => {
      const updated = await api.runIntegration('financial-management');
      const note = updated.lastAction?.summary ?? 'Synced.';
      pushToast({ tone: 'success', message: note });
      setHubIntegrations((prev) => prev.map((i) => (i.id === updated.id ? updated : i)));
      return note;
    },
    onRunError: (err) => pushToast({ tone: 'error', title: 'Could not sync', message: err.message }),
  });
  const financialConfig = financialState.config;
  const setFinancialConfig = financialState.setConfig;
  const setFinancialError = financialState.setError;
  const onSaveFinancial = financialState.save;
  const onRunFinancial = financialState.run;
  // Exchange the Plaid public_token for a stored bank connection, then refresh + land on the dashboard.
  // A connection just landed (embedded exchange, or a hosted session the server already exchanged):
  // refresh config + items, clear the stashed token, toast, and go to the dashboard.
  const refreshAfterConnect = useCallback(
    async (message: string) => {
      const [cfg, items] = await Promise.all([api.getFinancialConfig(), api.listFinancialItems()]);
      setFinancialConfig(cfg);
      setFinancialItems(items);
      clearStoredLinkToken();
      setHostedPending(false);
      setFinancialConnecting(false);
      // Stay on the Financial Management settings screen — the connected bank now shows in the
      // "Connected" list at the top of the panel. Don't yank the user over to the Finances dashboard.
      pushToast({ tone: 'success', message });
      // A reconnect (not a fresh connect) should pull fresh data immediately: run the sync now so the
      // "last updated" time + balances refresh without waiting for tonight's job.
      if (reconnectSyncRef.current) {
        reconnectSyncRef.current = false;
        void onRunFinancial();
      }
    },
    [api, pushToast, onRunFinancial, setFinancialConfig],
  );

  // Resolve a hosted-connect poll result. Returns true when the session is done (something connected, or
  // everything was already connected), false when still pending. Centralizes the duplicate handling.
  const finishHostedResult = useCallback(
    async (connected: number, duplicates: number): Promise<boolean> => {
      // Another poller already finalized this session — treat as resolved, but don't toast/refresh twice.
      if ((connected > 0 || duplicates > 0) && hostedDoneRef.current) return true;
      if (connected > 0 || duplicates > 0) hostedDoneRef.current = true;
      if (connected > 0) {
        const dupNote = duplicates > 0 ? ` (${duplicates} already connected)` : '';
        await refreshAfterConnect(`Connected ${connected} account${connected === 1 ? '' : 's'}${dupNote}. Syncing tonight — or use “Sync now”.`);
        return true;
      }
      if (duplicates > 0) {
        clearStoredLinkToken();
        setHostedPending(false);
        try {
          setFinancialItems(await api.listFinancialItems());
        } catch {
          /* non-fatal */
        }
        pushToast({ tone: 'info', title: 'Already connected', message: 'That bank is already connected to Lapo.' });
        return true;
      }
      return false;
    },
    [api, pushToast, refreshAfterConnect],
  );

  const afterPlaidSuccess = useCallback(
    async (publicToken: string) => {
      try {
        await api.exchangePlaidPublicToken(publicToken);
        await refreshAfterConnect('Bank connected. Syncing tonight — or use “Sync now”.');
      } catch (err) {
        // A 409 means Plaid re-linked a bank we already have — inform, don't alarm.
        const already = err instanceof ApiError && err.status === 409;
        pushToast(
          already
            ? { tone: 'info', title: 'Already connected', message: 'That bank is already connected to Lapo.' }
            : { tone: 'error', title: 'Could not connect', message: humanizeApiError(err) },
        );
      } finally {
        setFinancialConnecting(false);
      }
    },
    [api, pushToast, refreshAfterConnect],
  );

  // Open Plaid Link for a given link token. `receivedRedirectUri` resumes an OAuth (redirect-based) bank
  // flow on the return page. The link token is stashed so an OAuth redirect (which navigates the whole
  // tab away and back) can resume after reload (DL-09 web flow).
  const runPlaidLink = useCallback(
    async (linkToken: string, receivedRedirectUri?: string) => {
      const plaid = await loadPlaidLink();
      if (!plaid) {
        pushToast({ tone: 'error', title: 'Plaid unavailable', message: 'Could not load Plaid Link. Check your connection and try again.' });
        return;
      }
      storeLinkToken(linkToken);
      const handler = plaid.create({
        token: linkToken,
        ...(receivedRedirectUri ? { receivedRedirectUri } : {}),
        onSuccess: (publicToken: string) => void afterPlaidSuccess(publicToken),
        onExit: () => {
          clearStoredLinkToken();
          setFinancialConnecting(false);
          setReconnectingItemId(undefined);
          reconnectSyncRef.current = false; // abandoned Link → drop any pending reconnect-sync intent
        },
      });
      handler.open();
    },
    [afterPlaidSuccess, pushToast],
  );

  const onConnectFinancial = useCallback(async () => {
    hostedDoneRef.current = false; // fresh connect — re-arm the single-finish guard
    setFinancialConnecting(true);
    setFinancialError(undefined);
    try {
      if (isTauri()) {
        // Native: OAuth can't complete in the WebView, so open Plaid HOSTED Link in the system browser
        // and poll for completion (the server exchanges finished tokens). No in-WebView OAuth round-trip.
        const { openUrl } = await import('@tauri-apps/plugin-opener');
        pushToast({ tone: 'success', message: 'Opening your browser to connect securely…' });
        const res = await hostedPlaidConnect({
          createToken: () => api.createHostedPlaidLink(),
          complete: (lt) => api.completeHostedPlaid(lt),
          openUrl: (u) => openUrl(u),
          onOpened: (lt) => {
            storeLinkToken(lt);
            setHostedPending(true);
          },
        });
        const resolved = await finishHostedResult(res.connected, res.duplicates);
        if (!resolved && res.timedOut) {
          pushToast({ tone: 'info', title: 'Still finishing?', message: 'Complete the connection in your browser — Lapo will finish it automatically when you return.' });
        }
        setFinancialConnecting(false);
      } else {
        const { linkToken } = await api.createPlaidLinkToken();
        await runPlaidLink(linkToken);
      }
    } catch (err) {
      setFinancialError(humanizeApiError(err));
      pushToast({ tone: 'error', title: 'Could not connect', message: humanizeApiError(err) });
      setFinancialConnecting(false);
    }
  }, [api, pushToast, runPlaidLink, finishHostedResult, setFinancialError]);

  // Reconnect a disconnected bank via Plaid Link UPDATE MODE: re-authenticate the existing item in place
  // (same item_id → exchange resets it to active). Mirrors onConnectFinancial's web/native split but mints
  // the token from the item's stored access token. On success, refreshAfterConnect kicks a sync so fresh
  // balances + a new "last updated" time land immediately (reconnectSyncRef gates that).
  const onReconnectFinancial = useCallback(
    async (itemId: string) => {
      hostedDoneRef.current = false; // re-arm the single-finish guard for this re-auth session
      reconnectSyncRef.current = true;
      setReconnectingItemId(itemId);
      setFinancialError(undefined);
      try {
        if (isTauri()) {
          const { openUrl } = await import('@tauri-apps/plugin-opener');
          pushToast({ tone: 'success', message: 'Opening your browser to reconnect securely…' });
          const res = await hostedPlaidConnect({
            createToken: () => api.createHostedPlaidUpdateLink(itemId),
            complete: (lt) => api.completeHostedPlaid(lt),
            openUrl: (u) => openUrl(u),
            onOpened: (lt) => {
              storeLinkToken(lt);
              setHostedPending(true);
            },
          });
          const resolved = await finishHostedResult(res.connected, res.duplicates);
          if (!resolved && res.timedOut) {
            pushToast({ tone: 'info', title: 'Still finishing?', message: 'Complete the reconnection in your browser — Lapo will finish it automatically when you return.' });
          }
        } else {
          const { linkToken } = await api.createPlaidUpdateLinkToken(itemId);
          await runPlaidLink(linkToken);
        }
      } catch (err) {
        reconnectSyncRef.current = false;
        setFinancialError(humanizeApiError(err));
        pushToast({ tone: 'error', title: 'Could not reconnect', message: humanizeApiError(err) });
      } finally {
        setReconnectingItemId(undefined);
      }
    },
    [api, pushToast, runPlaidLink, finishHostedResult, setFinancialError],
  );

  // Auto-finisher (native): re-check the stashed hosted session. Runs on app refocus (returning from the
  // browser) and the cold-start effect. Silent while still pending — it's a background retry, not a user
  // action, so it never nags; it only speaks up (via finishHostedResult) once the connection resolves.
  const onFinishHostedConnect = useCallback(async () => {
    // PEEK (don't consume) — Plaid's Hosted Link session surfaces the public_token a few seconds after
    // the user taps done, so the first poll on return is often still pending. Keeping the token stashed
    // lets each subsequent refocus retry until it resolves (finishHostedResult clears it on success) or
    // the 15-min TTL lapses. Consuming it here (the old bug) dropped the token after one premature poll,
    // so the connect silently never finished on the phone.
    const linkToken = peekStoredLinkToken();
    if (!linkToken) {
      setHostedPending(false);
      return;
    }
    try {
      const r = await api.completeHostedPlaid(linkToken);
      await finishHostedResult(r.connected, r.duplicates);
      // Not resolved yet → stay silent; the token is still stashed and the next refocus retries.
    } catch {
      /* transient — the next refocus retries */
    }
  }, [api, finishHostedResult]);

  // Cancel a pending hosted connect: discard the stashed session token so the auto-finisher stops and the
  // "Finishing…" state clears. Any bank the user did finish still surfaces on the next sync (the item was
  // already exchanged server-side); this only abandons an unfinished attempt.
  const onCancelHostedConnect = useCallback(() => {
    clearStoredLinkToken();
    setHostedPending(false);
    setFinancialConnecting(false);
    pushToast({ tone: 'info', title: 'Connection canceled', message: 'You can connect a bank whenever you’re ready.' });
  }, [pushToast]);

  // On refocus (returning from the system browser), auto-attempt to finish a pending hosted connect.
  useEffect(() => {
    if (!hostedPending) return;
    const onFocus = (): void => {
      if (document.visibilityState === 'visible') void onFinishHostedConnect();
    };
    document.addEventListener('visibilitychange', onFocus);
    return () => document.removeEventListener('visibilitychange', onFocus);
  }, [hostedPending, onFinishHostedConnect]);

  // Native cold-start resume: returning from the hosted-link browser cold-starts the WebView (dropping
  // the in-flight poll + React state), but the link token survives in localStorage. On startup, if one
  // is stashed, poll to finish the connect (the session is done by now). This is the reliable path.
  useEffect(() => {
    if (!session || !API_URL || !isTauri()) return;
    const token = takeStoredLinkToken();
    if (!token) return;
    storeLinkToken(token); // keep it so the "Finish connecting" button can retry if we don't finish here
    setHostedPending(true);
    setFinancialConnecting(true); // reflect the in-progress finish so the panel doesn't look idle
    let cancelled = false;
    void (async () => {
      // Poll fast (~1.2s) so the account lands within a second or two of returning to the app.
      for (let i = 0; i < 40 && !cancelled; i++) {
        try {
          const r = await api.completeHostedPlaid(token);
          if (await finishHostedResult(r.connected, r.duplicates)) return;
        } catch {
          /* keep trying — the session may still be finalizing */
        }
        await new Promise((res) => setTimeout(res, 1200));
      }
      if (!cancelled) setFinancialConnecting(false); // gave up waiting; leave it to a manual retry
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session]);

  // Plaid OAuth return: an OAuth bank redirected the tab back to /plaid-oauth?oauth_state_id=… . Resume
  // Link with the stashed token + the received URL, then clean the address bar. Waits for the session
  // (needed to exchange), so it fires once auth is restored.
  useEffect(() => {
    if (!session || !API_URL || !isPlaidOAuthReturn()) return;
    const receivedRedirectUri = window.location.href;
    const token = takeStoredLinkToken();
    window.history.replaceState({}, '', '/');
    if (!token) {
      pushToast({ tone: 'error', title: 'Could not resume', message: 'Your bank connection couldn’t be resumed. Please try connecting again.' });
      return;
    }
    void runPlaidLink(token, receivedRedirectUri);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session, runPlaidLink]);

  const [financialImporting, setFinancialImporting] = useState(false);
  const onImportMonarch = useCallback(
    async (file: File) => {
      setFinancialImporting(true);
      try {
        const csv = await file.text();
        const r = await api.importMonarchCsv(csv, file.name);
        pushToast({ tone: 'success', message: `Monarch import: ${r.rulesCreated} rules · ${r.categoriesSeeded} categories from ${r.parsed} rows.` });
        // Refresh config so the panel shows the persisted "last import" reference immediately.
        setFinancialConfig(await api.getFinancialConfig());
      } catch (err) {
        pushToast({ tone: 'error', title: 'Import failed', message: humanizeApiError(err) });
      } finally {
        setFinancialImporting(false);
      }
    },
    [api, pushToast, setFinancialConfig],
  );
  const onDisconnectFinancialItem = useCallback(
    async (itemId: string) => {
      try {
        await api.removeFinancialItem(itemId);
        setFinancialItems(await api.listFinancialItems());
        pushToast({ tone: 'success', message: 'Bank disconnected.' });
      } catch (err) {
        pushToast({ tone: 'error', title: 'Could not disconnect', message: humanizeApiError(err) });
      }
    },
    [api, pushToast],
  );
  // Cardholder aliases (Integrations → Financial Management). Stable identity so the panel's load effect
  // doesn't re-fire each render.
  const loadFinancialCardholders = useCallback(() => api.listFinancialCardholders(), [api]);
  const onSaveFinancialCardholder = useCallback(
    async (raw: string, alias: string) => {
      try {
        const cfg = financialConfig ?? (await api.getFinancialConfig());
        const cardholderAliases = { ...(cfg.cardholderAliases ?? {}) };
        if (alias.trim()) cardholderAliases[raw] = alias.trim();
        else delete cardholderAliases[raw];
        const next = await api.putFinancialConfig({ ...cfg, cardholderAliases });
        setFinancialConfig(next);
        pushToast({ tone: 'success', message: `Saved “${alias.trim() || raw}”.` });
      } catch (err) {
        pushToast({ tone: 'error', title: 'Could not save', message: humanizeApiError(err) });
      }
    },
    [api, financialConfig, pushToast, setFinancialConfig],
  );

  // Re-pull config + items so a change made on another device (e.g. a Monarch import on web) shows here
  // without an app restart — the summary/rules live on the config. Called by App's integration-detail
  // refresh effect when the Financial Management page opens.
  const refreshFinancialFromHub = useCallback(async () => {
    const [c, items] = await Promise.all([api.getFinancialConfig(), api.listFinancialItems().catch(() => [])]);
    setFinancialConfig(c);
    setFinancialItems(items);
  }, [api, setFinancialConfig]);

  const financial: FinancialSectionState | undefined =
    session && API_URL && financialConfig
      ? {
          config: financialConfig,
          saving: financialState.saving,
          running: financialState.running,
          error: financialState.error,
          lastRunNote: financialState.runNote,
        }
      : undefined;

  return {
    financialConfig,
    financialItems,
    financialConnecting,
    reconnectingItemId,
    hostedPending,
    financialImporting,
    financial,
    onSaveFinancial,
    onRunFinancial,
    onConnectFinancial,
    onReconnectFinancial,
    onCancelHostedConnect,
    onImportMonarch,
    onDisconnectFinancialItem,
    loadFinancialCardholders,
    onSaveFinancialCardholder,
    refreshFinancialFromHub,
  };
}
